// Wifi-style bars + colored number used to display network latency.
// Shared between the lobby (PartyPanel — under each member's avatar) and
// the in-game PingHud (per-row inside the right-bottom panel).
//
// Tiers (single source of truth, both views):
//   <60ms   green   4/4 bars
//   <120ms  yellow  3/4 bars
//   <200ms  orange  2/4 bars
//   ≥200ms  red     1/4 bars
//   0       grey    0/4 bars, label "AI" or "…"

const STYLES_ID = 'ping-badge-styles';
const STYLES = `
.ping-badge {
  display: inline-flex;
  align-items: flex-end;
  gap: 6px;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.ping-badge .bars {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 14px;
}
.ping-badge .bars > span {
  display: block;
  width: 3px;
  border-radius: 1px;
  background: var(--pb-empty, rgba(255,255,255,0.18));
  transition: background-color 140ms ease;
}
.ping-badge .bars > span.b1 { height: 4px; }
.ping-badge .bars > span.b2 { height: 7px; }
.ping-badge .bars > span.b3 { height: 10px; }
.ping-badge .bars > span.b4 { height: 14px; }
.ping-badge .bars > span.on { background: var(--pb-color, #BFE0FF); }
.ping-badge .ms {
  color: var(--pb-color, #BFE0FF);
  text-shadow: 0 -2px 0 rgba(0, 60, 90, 0.4);
}
.ping-badge.size-sm { font-size: 14px; }
.ping-badge.size-sm .bars { height: 10px; }
.ping-badge.size-sm .bars > span { width: 2px; }
.ping-badge.size-sm .bars > span.b1 { height: 3px; }
.ping-badge.size-sm .bars > span.b2 { height: 5px; }
.ping-badge.size-sm .bars > span.b3 { height: 7px; }
.ping-badge.size-sm .bars > span.b4 { height: 10px; }
`;

const COLOR_GREEN  = '#9FE5A0';
const COLOR_YELLOW = '#FFD87A';
const COLOR_ORANGE = '#FFB16B';
const COLOR_RED    = '#FF8A8A';
const COLOR_GREY   = '#9FB7C2';

export interface PingBadgeOptions {
  /** Default 'md'. 'sm' shrinks the bars + font for dense lobby header use. */
  size?: 'sm' | 'md';
}

export interface PingBadgeHandle {
  /** Root element — caller appends to the desired host. */
  el: HTMLElement;
  /** Set the displayed ping. ms <= 0 with isAI true → "AI"; ms <= 0 without
   *  isAI → "…" placeholder. Idempotent — repeated calls with the same
   *  value are no-ops at the DOM level. */
  setPing(ms: number, isAI?: boolean): void;
  dispose(): void;
}

export function makePingBadge(opts: PingBadgeOptions = {}): PingBadgeHandle {
  ensureStyles();
  const root = document.createElement('span');
  root.className = 'ping-badge' + (opts.size === 'sm' ? ' size-sm' : '');
  root.innerHTML = `
    <span class="bars" aria-hidden="true">
      <span class="b1"></span><span class="b2"></span><span class="b3"></span><span class="b4"></span>
    </span>
    <span class="ms">…</span>
  `;
  const bars = root.querySelectorAll('.bars > span') as NodeListOf<HTMLElement>;
  const ms = root.querySelector('.ms') as HTMLElement;

  let lastMs = -1;
  let lastIsAI = false;

  const setPing = (rawMs: number, isAI = false): void => {
    const m = Math.max(0, Math.round(rawMs || 0));
    if (m === lastMs && isAI === lastIsAI) return;
    lastMs = m; lastIsAI = isAI;

    let level: 0 | 1 | 2 | 3 | 4;
    let color: string;
    let label: string;
    if (m <= 0) {
      level = 0;
      color = COLOR_GREY;
      label = isAI ? 'AI' : '…';
    } else if (m < 60) {
      level = 4; color = COLOR_GREEN;  label = `${m}ms`;
    } else if (m < 120) {
      level = 3; color = COLOR_YELLOW; label = `${m}ms`;
    } else if (m < 200) {
      level = 2; color = COLOR_ORANGE; label = `${m}ms`;
    } else {
      level = 1; color = COLOR_RED;    label = `${m}ms`;
    }
    root.style.setProperty('--pb-color', color);
    for (let i = 0; i < bars.length; i++) {
      const on = i < level;
      const bar = bars[i]!;
      if (bar.classList.contains('on') !== on) {
        bar.classList.toggle('on', on);
      }
    }
    if (ms.textContent !== label) ms.textContent = label;
  };

  return {
    el: root,
    setPing,
    dispose(): void { root.remove(); }
  };
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const s = document.createElement('style');
  s.id = STYLES_ID;
  s.textContent = STYLES;
  document.head.appendChild(s);
}
